import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { systemSettings } from '@/mocks/settings';
import { registerLaundry } from '@/services/authService';
import LocationPicker, { type MapPoint } from '@/components/feature/LocationPicker';
import Alert from '@/components/base/Alert';
import Button from '@/components/base/Button';
import Checkbox from '@/components/base/Checkbox';
import FileUpload from '@/components/base/FileUpload';
import Input from '@/components/base/Input';
import Select from '@/components/base/Select';
import Textarea from '@/components/base/Textarea';
import TimeInput from '@/components/base/TimeInput';
import AuthLayout from '../components/AuthLayout';
import FormSection from './components/FormSection';
import ReviewPanel from './components/ReviewPanel';

interface RegisterFormState {
  laundryName: string;
  description: string;
  phone: string;
  email: string;
  password: string;
  confirmPassword: string;
  cityId: string;
  district: string;
  street: string;
  building: string;
  coverage: string[];
  mapPoint: MapPoint | null;
  fromTime: string;
  toTime: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const INITIAL: RegisterFormState = {
  laundryName: '',
  description: '',
  phone: '',
  email: '',
  password: '',
  confirmPassword: '',
  cityId: '',
  district: '',
  street: '',
  building: '',
  coverage: [],
  mapPoint: null,
  fromTime: '09:00',
  toTime: '21:00',
};

export default function RegisterPage() {
  const { t } = useTranslation();
  const [form, setForm] = useState<RegisterFormState>(INITIAL);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [reference, setReference] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [documents, setDocuments] = useState<Record<string, File | null>>({
    commercial: null,
    id: null,
    'extra-0': null,
    'extra-1': null,
  });

  const selectedCity = systemSettings.cities.find((c) => c.id === form.cityId);

  function update<K extends keyof RegisterFormState>(key: K, value: RegisterFormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key as string];
      return next;
    });
  }

  const toggleCoverage = (district: string, checked: boolean) => {
    const next = checked
      ? [...form.coverage, district]
      : form.coverage.filter((d) => d !== district);
    update('coverage', next);
  };

  const uploadLabels = {
    upload: t('auth.register.uploadFile'),
    replace: t('auth.register.replaceFile'),
    remove: t('auth.register.removeFile'),
    noFile: t('auth.register.noFile'),
    tooLarge: t('upload.tooLarge'),
    wrongType: t('upload.wrongType'),
  };

  const validate = () => {
    const next: Record<string, string> = {};
    if (!form.laundryName.trim()) next.laundryName = t('common.required');
    if (!form.phone.trim()) next.phone = t('common.required');
    if (!form.email.trim()) next.email = t('common.required');
    else if (!EMAIL_RE.test(form.email.trim())) next.email = t('common.invalidEmail');
    if (!form.password) next.password = t('common.required');
    else if (form.password.length < 8) next.password = t('auth.register.passwordHint');
    if (!form.confirmPassword) next.confirmPassword = t('common.required');
    else if (form.confirmPassword !== form.password)
      next.confirmPassword = t('auth.register.passwordMismatch');
    if (!form.cityId) next.cityId = t('common.required');
    if (!form.district.trim()) next.district = t('common.required');
    if (!form.street.trim()) next.street = t('common.required');
    if (!form.building.trim()) next.building = t('common.required');
    if (form.coverage.length === 0) next.coverage = t('common.required');
    if (!form.fromTime) next.fromTime = t('common.required');
    if (!form.toTime) next.toTime = t('common.required');
    if (!documents.commercial) next.commercial = t('common.required');
    if (!documents.id) next.id = t('common.required');
    systemSettings.additionalDocuments.forEach((_, idx) => {
      if (!documents[`extra-${idx}`]) next[`extra-${idx}`] = t('common.required');
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);
    if (!validate()) {
      setFormError(t('auth.register.errorFix'));
      return;
    }
    setSubmitting(true);
    const result = await registerLaundry({ ...form });
    setSubmitting(false);
    setReference(result.reference);
  };

  if (reference) {
    return (
      <AuthLayout maxWidth="max-w-2xl">
        <ReviewPanel reference={reference} />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout maxWidth="max-w-3xl">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-foreground-950 md:text-2xl">{t('auth.register.title')}</h1>
        <p className="mt-1.5 text-sm leading-relaxed text-foreground-500">{t('auth.register.subtitle')}</p>
      </div>

      <form className="space-y-5" onSubmit={onSubmit} noValidate>
        {formError ? <Alert variant="error">{formError}</Alert> : null}

        <FormSection icon="ri-store-2-line" title={t('auth.register.section.laundry')}>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Input
              id="reg-name"
              name="laundry_name"
              label={t('auth.register.name')}
              requiredMark
              value={form.laundryName}
              error={errors.laundryName}
              onChange={(e) => update('laundryName', e.target.value)}
            />
            <Input
              id="reg-phone"
              name="phone"
              label={t('auth.register.phone')}
              type="tel"
              ltr
              leadingIcon="ri-phone-line"
              requiredMark
              value={form.phone}
              error={errors.phone}
              onChange={(e) => update('phone', e.target.value)}
            />
            <Input
              id="reg-email"
              name="email"
              label={t('auth.register.email')}
              type="email"
              ltr
              leadingIcon="ri-mail-line"
              requiredMark
              value={form.email}
              error={errors.email}
              onChange={(e) => update('email', e.target.value)}
            />
            <FileUpload
              id="reg-logo"
              name="logo"
              label={t('auth.register.logo')}
              file={logoFile}
              onChange={setLogoFile}
              accept=".png,.jpg,.jpeg,.svg"
              maxSizeMB={systemSettings.upload.maxSizeMB}
              hint={t('auth.register.uploadRule')}
              labels={uploadLabels}
            />
            <div className="md:col-span-2">
              <Textarea
                id="reg-description"
                name="description"
                label={t('auth.register.description')}
                value={form.description}
                onChange={(e) => update('description', e.target.value)}
              />
            </div>
            <Input
              id="reg-password"
              name="password"
              label={t('auth.register.password')}
              type="password"
              ltr
              autoComplete="new-password"
              requiredMark
              hint={t('auth.register.passwordHint')}
              value={form.password}
              error={errors.password}
              onChange={(e) => update('password', e.target.value)}
            />
            <Input
              id="reg-confirm"
              name="confirm_password"
              label={t('auth.register.confirmPassword')}
              type="password"
              ltr
              autoComplete="new-password"
              requiredMark
              value={form.confirmPassword}
              error={errors.confirmPassword}
              onChange={(e) => update('confirmPassword', e.target.value)}
            />
          </div>
        </FormSection>

        <FormSection
          icon="ri-map-pin-2-line"
          title={t('auth.register.section.address')}
          description={t('auth.register.mapHint')}
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <LocationPicker
                value={form.mapPoint}
                onChange={(point) => update('mapPoint', point)}
                address={{
                  cityId: form.cityId,
                  district: form.district,
                  street: form.street,
                  building: form.building,
                }}
              />
            </div>

            <Select
              id="reg-city"
              name="city"
              label={t('auth.register.city')}
              requiredMark
              placeholder={t('auth.register.cityPlaceholder')}
              value={form.cityId}
              error={errors.cityId}
              options={systemSettings.cities.map((c) => ({ value: c.id, label: c.name }))}
              onChange={(e) => {
                update('cityId', e.target.value);
                update('coverage', []);
                update('district', '');
              }}
            />
            <Input
              id="reg-district"
              name="district"
              label={t('auth.register.district')}
              requiredMark
              value={form.district}
              error={errors.district}
              onChange={(e) => update('district', e.target.value)}
            />
            <Input
              id="reg-street"
              name="street"
              label={t('auth.register.street')}
              requiredMark
              value={form.street}
              error={errors.street}
              onChange={(e) => update('street', e.target.value)}
            />
            <Input
              id="reg-building"
              name="building"
              label={t('auth.register.building')}
              requiredMark
              value={form.building}
              error={errors.building}
              onChange={(e) => update('building', e.target.value)}
            />

            <div className="md:col-span-2">
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-sm font-medium text-foreground-800">
                  {t('auth.register.coverage')}
                  <span className="text-rose-600"> *</span>
                </span>
                <span className="text-xs text-foreground-500">{t('auth.register.coverageHint')}</span>
              </div>
              {selectedCity ? (
                <div className="grid grid-cols-2 gap-2.5 rounded-md border border-background-200 bg-background-50 p-3.5 sm:grid-cols-3">
                  {selectedCity.districts.map((district) => (
                    <Checkbox
                      key={district}
                      label={district}
                      name="coverage"
                      checked={form.coverage.includes(district)}
                      onChange={(checked) => toggleCoverage(district, checked)}
                    />
                  ))}
                </div>
              ) : (
                <p className="rounded-md border border-dashed border-background-300 bg-background-100 px-3.5 py-3 text-sm text-foreground-500">
                  {t('auth.register.coverageEmpty')}
                </p>
              )}
              {errors.coverage ? <p className="mt-1 text-xs text-rose-600">{errors.coverage}</p> : null}
            </div>
          </div>
        </FormSection>

        <FormSection icon="ri-time-line" title={t('auth.register.section.hours')}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TimeInput
              id="reg-from"
              name="from_time"
              label={t('auth.register.fromTime')}
              requiredMark
              value={form.fromTime}
              error={errors.fromTime}
              onChange={(e) => update('fromTime', e.target.value)}
            />
            <TimeInput
              id="reg-to"
              name="to_time"
              label={t('auth.register.toTime')}
              requiredMark
              value={form.toTime}
              error={errors.toTime}
              onChange={(e) => update('toTime', e.target.value)}
            />
          </div>
        </FormSection>

        <FormSection
          icon="ri-file-shield-2-line"
          title={t('auth.register.section.docs')}
          description={t('auth.register.docsHint')}
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FileUpload
              id="reg-doc-commercial"
              name="doc_commercial"
              label={t('auth.register.doc.commercial')}
              requiredMark
              file={documents.commercial}
              onChange={(file) => setDocuments((prev) => ({ ...prev, commercial: file }))}
              accept={systemSettings.upload.accept}
              maxSizeMB={systemSettings.upload.maxSizeMB}
              error={errors.commercial}
              labels={uploadLabels}
            />
            <FileUpload
              id="reg-doc-id"
              name="doc_id"
              label={t('auth.register.doc.id')}
              requiredMark
              file={documents.id}
              onChange={(file) => setDocuments((prev) => ({ ...prev, id: file }))}
              accept={systemSettings.upload.accept}
              maxSizeMB={systemSettings.upload.maxSizeMB}
              error={errors.id}
              labels={uploadLabels}
            />
            {systemSettings.additionalDocuments.map((docName, idx) => (
              <FileUpload
                key={docName}
                id={`reg-doc-extra-${idx}`}
                name={`doc_extra_${idx}`}
                label={docName}
                requiredMark
                file={documents[`extra-${idx}`] ?? null}
                onChange={(file) => setDocuments((prev) => ({ ...prev, [`extra-${idx}`]: file }))}
                accept={systemSettings.upload.accept}
                maxSizeMB={systemSettings.upload.maxSizeMB}
                error={errors[`extra-${idx}`]}
                labels={uploadLabels}
              />
            ))}
          </div>
          <p className="mt-4 text-xs text-foreground-500">{t('auth.register.uploadRule')}</p>
        </FormSection>

        <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Link to="/login" className="text-sm font-medium text-foreground-600 hover:text-foreground-900">
            {t('auth.register.reviewToLogin')}
          </Link>
          <Button type="submit" variant="primary" size="lg" loading={submitting}>
            {t('auth.register.submit')}
          </Button>
        </div>

        <Alert variant="info" title={t('common.demoData')}>
          {t('auth.register.demoNotice')}
        </Alert>
      </form>
    </AuthLayout>
  );
}